import { logger } from '@adornis/base/logging.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { format, subDays, subHours } from 'date-fns';
import type { TemplateResult } from 'lit';
import { getWorkshopByID } from '../api/workshop.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import type { Appointment } from '../entities/appointment.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import type { Product } from '../entities/products.js';
import { getMergedProductSettings, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { mongo } from '../server/mongo.js';
import {
  getMeetingLinkHtml,
  templateUsesMeetingCodeOrPassword,
  getNotificationType,
  sendWhatsappWithEmailFallback,
  toWhatsappBodyParam,
} from './_helper.js';

defineMailTemplate(MailTemplateType.MEETING_INFOS, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Termininformationen',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>die Veranstaltung findet in wenigen Tagen statt. Der Termin findet digital in der Form eines Videocall unter folgendem Link statt:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>The event will take place in a few days. The appointment will be held digitally in the form of a video call at the following link:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS),
  attachments: [],
  displayName: 'Termin Informationen Online',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS),
});

defineMailTemplate(MailTemplateType.MEETING_INFOS_2, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Erinnerung: Ihre Veranstaltung beginnt heute / Reminder: Your workshop starts today',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>dies ist eine kurze Erinnerung, dass die Veranstaltung <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> heute stattfindet. Bitte seien Sie p&uuml;nktlich dabei!</p>
<p>Meeting-Link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>this is just a quick reminder that the workshop <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> will start today. Please join us on time!</p>
<p>Meeting link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS_2),
  attachments: [],
  displayName: 'Termin Informationen Online (2. Versand)',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS_2),
});

defineMailTemplate(MailTemplateType.MEETING_INFOS_IN_PERSON, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Termininformationen',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>die Veranstaltung findet in wenigen Tagen vor Ort statt.</p>
<p>Veranstaltungsort: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>The event will take place in person in a few days.</p>
<p>Location: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS_IN_PERSON),
  attachments: [],
  displayName: 'Termin Informationen vor Ort',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS_IN_PERSON),
});

defineMailTemplate(MailTemplateType.MEETING_INFOS_2_IN_PERSON, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Erinnerung: Ihre Veranstaltung beginnt heute / Reminder: Your workshop starts today',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>dies ist eine kurze Erinnerung: Die Veranstaltung <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> beginnt heute und findet vor Ort statt. Bitte seien Sie p&uuml;nktlich dabei!</p>
<p>Veranstaltungsort: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>This is just a quick reminder: The workshop <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> starts today and takes place in person. Please arrive on time!</p>
<p>Location: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS_2_IN_PERSON),
  attachments: [],
  displayName: 'Termin Informationen vor Ort (2. Versand)',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS_2_IN_PERSON),
});

defineMailTemplate(MailTemplateType.MEETING_INFOS_HYBRID, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Termininformationen',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>die Veranstaltung findet in wenigen Tagen hybrid statt. Sie k&ouml;nnen vor Ort oder online teilnehmen.</p>
<p>Veranstaltungsort: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>Meeting-Link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>The event will take place in a few days in a hybrid format. You can attend in person or online.</p>
<p>Location: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>Meeting link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS_HYBRID),
  attachments: [],
  displayName: 'Termin Informationen Hybrid',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS_HYBRID),
});

defineMailTemplate(MailTemplateType.MEETING_INFOS_2_HYBRID, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Erinnerung: Ihre Veranstaltung beginnt heute / Reminder: Your workshop starts today',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>dies ist eine kurze Erinnerung: Die Veranstaltung <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> beginnt heute und findet hybrid statt. Sie k&ouml;nnen vor Ort oder online teilnehmen. Bitte seien Sie p&uuml;nktlich dabei!</p>
<p>Veranstaltungsort: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>Meeting-Link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>This is just a quick reminder: The workshop <strong>{{${MailTemplateVars.PRODUCT_NAME}}}</strong> starts today in a hybrid format. You can attend in person or online. Please join us on time!</p>
<p>Location: {{${MailTemplateVars.APPOINTMENT_LOCATION}}}</p>
<p>Meeting link: {{${MailTemplateVars.MEETING_LINK}}}</p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.MEETING_INFOS_2_HYBRID),
  attachments: [],
  displayName: 'Termin Informationen Hybrid (2. Versand)',
  editorHint: () => institutionMailHint(MailTemplateType.MEETING_INFOS_2_HYBRID),
});

export function resolveMeetingInfoTemplateType(
  presenceType: Product['presenceType'] | null | undefined,
  kind: 'first' | 'second' | undefined,
): MailTemplateType {
  switch (presenceType) {
    case 'in_person':
      return kind === 'second' ? MailTemplateType.MEETING_INFOS_2_IN_PERSON : MailTemplateType.MEETING_INFOS_IN_PERSON;
    case 'hybrid':
      return kind === 'second' ? MailTemplateType.MEETING_INFOS_2_HYBRID : MailTemplateType.MEETING_INFOS_HYBRID;
    default:
      // Online, "not_selected", null and undefined use the existing Online templates to preserve customised texts.
      return kind === 'second' ? MailTemplateType.MEETING_INFOS_2 : MailTemplateType.MEETING_INFOS;
  }
}

function buildMeetingInfoWhatsappParams(
  mailTemplateType: MailTemplateType,
  user: User,
  workshop: NarrowedEntity<
    Workshop,
    {
      name: 1;
      _class: 1;
      dateIsToBeDetermined: 1;
      appointments: { name: 1; start: 1; end: 1; location: 1 };
      settings: { courseLocation: 1; inheritedCourseLocation: 1 };
    }
  >,
  appointment: NarrowedEntity<Appointment, { start: 1; location: 1; meetingLink: { link: 1; code: 1; password: 1 } }>,
): string[] {
  const parameters = [
    InstitutionMailTemplate.mapFirstNameVar(user) || '-',
    InstitutionMailTemplate.mapLastNameVar(user) || '-',
    workshop.name || '-',
    appointment.start?.toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      dateStyle: 'medium',
      timeStyle: 'short',
    }) || '-',
  ];
  const location =
    appointment.location || workshop.settings?.courseLocation || workshop.settings?.inheritedCourseLocation?.[1] || '-';
  const ml = appointment.meetingLink;
  const meetingLink = ml?.link
    ? `${ml.link}${ml.code ? ` Code: ${ml.code}` : ''}${ml.password ? ` Passwort: ${ml.password}` : ''}`
    : '-';

  switch (mailTemplateType) {
    case MailTemplateType.MEETING_INFOS:
    case MailTemplateType.MEETING_INFOS_2:
      parameters.push(meetingLink);
      break;
    case MailTemplateType.MEETING_INFOS_IN_PERSON:
    case MailTemplateType.MEETING_INFOS_2_IN_PERSON:
      parameters.push(location);
      break;
    case MailTemplateType.MEETING_INFOS_HYBRID:
    case MailTemplateType.MEETING_INFOS_2_HYBRID:
      parameters.push(location, meetingLink);
      break;
    default:
      throw new Error('unsupported meeting info template: ' + mailTemplateType);
  }

  parameters.push(InstitutionMailTemplate.mapProductVarText(workshop));
  return parameters.map(toWhatsappBodyParam);
}

export async function notifyMeetingInfos(data: {
  participationID: string;
  appointmentID: string;
  kind?: 'first' | 'second';
}) {
  const participationCol = mongo().collection<Participation>(Participation._collectionName);
  const participation = await participationCol.raw.findOne<Participation>({ _id: data.participationID });
  if (!participation) throw new Error('participation not found: ' + data.participationID);
  if (![ParticipationState.in, ParticipationState.participated].includes(participation.state)) {
    logger.info(
      { participationID: data.participationID, state: participation.state, appointmentID: data.appointmentID },
      'Participation is neither enrolled nor participated. No meeting info notification was sent.',
    );
    return;
  }

  const userCol = mongo().collection<User>(User._collectionName);
  const user = await userCol.raw.findOne<User>({ _id: participation.userID });
  if (!user) throw new Error('user not found: ' + participation.userID);

  const workshop = await runInContext(serverContext.sessionID, () => getWorkshopByID(participation.productID)({
    _id: 1,
    _class: 1,
    isCanceled: 1,
    dateIsToBeDetermined: 1,
    institutionID: 1,
    name: 1,
    presenceType: 1,
    settings: {
      _id: 1,
      _class: 1,
      courseLocation: 1,
      inheritedCourseLocation: 1,
      meetingInfoMailLeadTime: 1,
      inheritedMeetingInfoMailLeadTime: 1,
      evaluationLink: 1,
      inheritedEvaluationLink: 1,
      evaluationLinkCode: 1,
      inheritedEvaluationLinkCode: 1,
    },
    appointments: {
      _id: 1,
      name: 1,
      start: 1,
      end: 1,
      location: 1,
      lastReminderMailSent: 1,
      meetingLink: { link: 1, code: 1, password: 1 },
    },
  }));

  if (!workshop) {
    logger.info(
      { _id: participation.productID },
      'No workshop found for given id. Therefore no meetingInfoMails were sent.',
    );
    throw new Error('No workshop found for given id');
  }

  if (workshop.isCanceled) {
    logger.info({ productID: workshop._id, participationID: data.participationID }, 'Workshop is canceled. No meeting info notification was sent.');
    return;
  }

  const appointment = workshop.appointments?.find(a => a._id === data.appointmentID);
  if (!appointment) throw new Error('appointment not found: ' + data.appointmentID);

  // Use the registered institution read to retrieve color definitions
  const institution = await getInstitutionByID(workshop.institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) {
    logger.info({ institutionID: workshop.institutionID }, "Couldn't find institution. No meeting info mail was sent.");
    throw new Error('institution not found');
  }

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplateType = resolveMeetingInfoTemplateType(workshop.presenceType, data.kind);
  const mailTemplate = await getMailTemplateForInstitution(
    workshop.institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.PRODUCT_NAME]: workshop.name,
      [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVar(workshop as Workshop),
      [MailTemplateVars.PRODUCT_CARD_EN]: InstitutionMailTemplate.mapProductVar(workshop as Workshop, 'en'),
      [MailTemplateVars.MEETING_LINK]: getMeetingLinkHtml(appointment as any, {
        omitCodeAndPassword: templateUsesMeetingCodeOrPassword({ subject: mailTemplate.subject, content: mailTemplate.content }),
      }),
      [MailTemplateVars.MEETING_CODE]: appointment.meetingLink?.code ?? '',
      [MailTemplateVars.MEETING_PASSWORD]: appointment.meetingLink?.password ?? '',
      [MailTemplateVars.APPOINTMENT_LOCATION]:
        appointment.location ||
        workshop.settings?.courseLocation ||
        workshop.settings?.inheritedCourseLocation?.[1] ||
        '',
      [MailTemplateVars.EVENT_LOCATION]:
        workshop.settings?.courseLocation || workshop.settings?.inheritedCourseLocation?.[1] || '',
      [MailTemplateVars.EVENT_DATES]: appointment.start ? format(appointment.start, 'dd.MM.yyyy') : 'tbd',
      [MailTemplateVars.EVALUATION_LINK]:
        workshop.settings.evaluationLink ?? workshop.settings.inheritedEvaluationLink?.[1] ?? '',
      [MailTemplateVars.EVALUATION_CODE]:
        workshop.settings.evaluationLinkCode ?? workshop.settings.inheritedEvaluationLinkCode?.[1] ?? '',
    };

    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });
    if (!mailContent) throw new Error('mail template has no content!');
    await sendInstitutionEmail({
      institutionID: workshop.institutionID,
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, workshop.institutionID);
  if (notificationType === 'whatsapp') {
    const result = await sendWhatsappWithEmailFallback({
      institutionID: workshop.institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType,
      parameters: buildMeetingInfoWhatsappParams(mailTemplateType, user, workshop, appointment),
      sendEmail,
    });
    if (!result.ok) throw new Error('whatsapp send failed: ' + (result.error ?? 'unknown'));
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}

/**
 * Sends meeting info mails for a participation if the scheduled auto-mail send date has already passed.
 * Used when participants are enrolled after the normal meeting info mail dispatch (e.g. admin enrollment, admin state change).
 */
export async function sendMeetingInfoMailsIfAlreadySent(participationID: string, productID: string) {
  // Callers fire-and-forget this helper — without a top-level guard a thrown setup error
  // (workshop lookup, settings aggregation) becomes a silent unhandled rejection and the
  // late-joiner never receives the mail. Log instead so we can see misses.
  try {
    const workshop = await runInContext(serverContext.sessionID, () => loadRawEntityDataByIDOrFail(Workshop, productID, {
      _id: 1,
      appointments: { _id: 1, start: 1 },
    }));

    const productSettings = await getMergedProductSettings(productID, [
      'meetingInfoMailLeadTime',
      'secondMeetingInfoMailLeadTimeHours',
    ]);

    const now = new Date();
    for (const appointment of workshop.appointments ?? []) {
      if (!appointment.start) continue;

      const fire = (kind: 'first' | 'second') => {
        notifyMeetingInfos({ participationID, appointmentID: appointment._id, kind }).catch(err =>
          logger.error(
            { err, participationID, productID, appointmentID: appointment._id, kind },
            'late meeting-info dispatch failed',
          ),
        );
      };

      const firstLeadDays = productSettings.meetingInfoMailLeadTime;
      if (typeof firstLeadDays === 'number' && firstLeadDays >= 0) {
        if (now > subDays(appointment.start, firstLeadDays)) fire('first');
      }

      const secondLeadHours = productSettings.secondMeetingInfoMailLeadTimeHours;
      if (typeof secondLeadHours === 'number' && secondLeadHours >= 0) {
        if (now > subHours(appointment.start, secondLeadHours)) fire('second');
      }
    }
  } catch (err) {
    logger.error({ err, participationID, productID }, 'sendMeetingInfoMailsIfAlreadySent setup failed');
  }
}
